import type {
  BotSequenceMode,
  BotSequenceType,
  CortexCatalog,
  LocationVarKey,
  MergedLocationVar,
  PolicyFieldCandidate,
  WeeklyPeriod
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

//LINK - apps/server/src/API/Cortex/Cortex.Controller.ts

// ─── Migration DTOs ─────────────────────────────────────────────────────────
// Mirrored from CortexMigration.Service so studio doesn't import server code.

export type MigrationScope = "org" | "group" | "location" | "brand";

export interface MigrationPreviewRow {
  scope: MigrationScope;
  scopeId: number;
  scopeLabel: string;
  fromValue: string | null;
  currentToValue: string | null;
  willWrite: string | null;
  /** Human-readable rendering of `fromValue` for the review UI (no raw JSON). */
  fromValueDisplay: string | null;
  /** Human-readable rendering of `currentToValue`. */
  currentToValueDisplay: string | null;
  /** Human-readable rendering of `willWrite`. */
  willWriteDisplay: string | null;
  alreadyMatches: boolean;
  /** Row skipped (not written) and surfaced for review. See `quarantineReason`. */
  quarantined: boolean;
  /** Why quarantined — `non-boolean-value`, `conflicting-sources`, `unparseable-hours`, `uncertain-hours`, `unclear-keypad`, `invalid-timezone`, `invalid-phone-number`, or `unmapped-variable` — or null when it wasn't. */
  quarantineReason: string | null;
  /** Caution on a row that WILL migrate but was a guess (e.g. `assumed-all-days` hours); null when none. */
  warning: string | null;
  /** Open review ticket already tracking this quarantined row (auto-opened on a prior apply); null when none. */
  ticket: { number: number; status: string; createdAt: string } | null;
  /**
   * Inheritance collapse (ENG-3209): `hoisted` = an org/group row standing in for many identical
   * location values; `inherits` = a legacy row NOT written because its scope now inherits the
   * same value; null = an ordinary row.
   */
  collapse: "hoisted" | "inherits" | "remove" | null;
  /** For a hoisted row: how many locations resolve through it. */
  covers: number | null;
  /** For an `inherits` / `remove` row: the scope it now inherits from. */
  inheritsFrom: "org" | "group" | null;
  /** For a `remove` row: the existing variable row apply deletes so the scope truly inherits. */
  removeVariableId: number | null;
}

/** How the review UI lets an operator fix a flagged row of this spec in place. */
export type MigrationInputKind = "hours" | "toggle" | "timezone" | "phone" | "text";

/** An operator's own value for one flagged row, written on Apply instead of becoming a review task. */
export interface MigrationOverride {
  specId: string;
  scope: MigrationScope;
  scopeId: number;
  /** Same format as the row's `willWrite`: weekly-grid JSON for hours, "1"/"0" for on/off, the plain value otherwise. */
  value: string;
}

/** A suggested weekly schedule for one legacy hours value; advisory until the operator confirms it. */
export interface HoursSuggestion {
  value: string;
  periods: WeeklyPeriod[] | null;
  /** True when the text isn't one facility's own weekly hours. */
  notHours: boolean;
}

export interface MigrationPreviewItem {
  specId: string;
  label: string;
  /** How a flagged row can be fixed in place; undefined for brand colors. */
  inputKind?: MigrationInputKind;
  fromKey?: LocationVarKey;
  toKey?: LocationVarKey;
  /** Target Schedule slug — present only for hours specs that write a Schedule. */
  scheduleTarget?: { slug: string; label: string };
  /** True when the spec targets the Brand entity's `colors` JSONB (ENG-2893). */
  brandColorsTarget?: true;
  rows: MigrationPreviewRow[];
  summary: {
    wouldWriteCount: number;
    alreadyMatchCount: number;
    noLegacyValueCount: number;
    quarantineCount: number;
    warningCount: number;
    /** Org/group rows written in place of many identical location rows. */
    hoistedCount: number;
    /** Legacy rows not written because their scope now inherits the same value. */
    inheritsCount: number;
    /** Existing explicit rows apply removes because their scope now inherits the same value. */
    removeCount: number;
  };
}

export interface MigrationPreview {
  groups: { groupLabel: string; items: MigrationPreviewItem[] }[];
  /** Open review tickets from previous migrations still needing attention (warns before re-migrating). */
  openTicketCount: number;
  /** ISO timestamp of the last Apply run for this org, or null if never migrated. */
  lastMigratedAt: string | null;
}

export interface MigrationApplyResult {
  written: number;
  skipped: number;
  /** Rows skipped because they were quarantined for review (not written). */
  quarantined: number;
  /** Rows written but carrying a caution to verify (e.g. hours where all days were assumed). */
  warned: number;
  /** 1 when this run opened the org's Details review ticket, else 0. */
  ticketsCreated: number;
  /** The org's open Details review ticket after this run, or null when there is nothing to review. */
  reviewTicketNumber: number | null;
  /** Rows added to the review ticket as tasks this run. */
  reviewTasksAdded: number;
  /** 1 when this run opened the org's "New in Snowy" ticket (one task per new toggle), else 0. */
  newFieldTicketsCreated: number;
  /** Flagged rows written from the operator's own value instead of becoming review tasks. */
  overridesApplied: number;
  /** Operator values that failed validation; those rows stay flagged and go on the review ticket. */
  overridesRejected: number;
}

export interface CortexTopicResponse {
  topic: unknown; // CortexTopic — avoid deep type pull at the API-helper boundary
  vars: MergedLocationVar[];
}

export interface CortexSaveSectionPatch {
  sectionId: string;
  /** Typed setting values — written directly. Keys are setting.key (not varKey). */
  settingValues?: Record<string, string>;
}

export interface CortexSaveRequest {
  orgId: number;
  groupId?: number;
  locationId?: number;
  topicId: string;
  sections: CortexSaveSectionPatch[];
}

export interface CortexSaveResponse {
  saved: LocationVarKey[];
  errors: { varKey: LocationVarKey; message: string }[];
}

export interface CortexRawSavePatch {
  key: LocationVarKey;
  value: string;
  source?: string | null;
}

export interface CortexRawSaveRequest {
  orgId: number;
  groupId?: number;
  locationId?: number;
  patches: CortexRawSavePatch[];
}

// ─── Policy-migration DTOs (mirror CortexPolicyMigration.Service) ────────────

export interface PolicyMigrationLocation {
  locationId: number;
  name: string;
  candidates: PolicyFieldCandidate[];
}

export interface PolicyMigrationNewField {
  mapKey: string;
  policyKey: string;
  label: string;
  group: string;
}

export interface PolicyMigrationChange {
  scope: string;
  mapKey: string;
  value: string;
}

export interface PolicyMigrationRun {
  at: string;
  appliedFieldCount: number;
  locationCount: number;
  failedFieldCount: number;
  changes: PolicyMigrationChange[];
}

export interface PolicyMigrationPreview {
  org: PolicyFieldCandidate[];
  locations: PolicyMigrationLocation[];
  newFields: PolicyMigrationNewField[];
  lastRun: PolicyMigrationRun | null;
  /** Part of the legacy-text scan failed; answers it would have found may be missing. Reload retries. */
  scanIncomplete: boolean;
}

export interface PolicyMigrationWrite {
  locationId?: number | null;
  policyKey: string;
  answers: Record<string, string>;
}

export interface PolicyMigrationApplyResult {
  appliedFieldCount: number;
  locationCount: number;
  failedFieldCount: number;
}

export interface PolicyNewFieldsTicketResult {
  created: boolean;
  ticketNumber: number | null;
  taskCount: number;
}

export interface MigrationStatus {
  detailsLastMigratedAt: string | null;
  policyLastRun: PolicyMigrationRun | null;
  unmigratedLastRun: UnmigratedReviewRun | null;
  sequencesLastRun: SequenceMigrationRun | null;
}

// ─── Sequences step DTOs ────────────────────────────────────────────────────
// Mirrored from CortexSequenceMigration.Service (ENG-3739).

/** What the step does with one row. Only create, refresh and remove write anything. */
export type SequenceMigrationRowStatus = "create" | "refresh" | "unchanged" | "keptEdited" | "keptSnowy" | "remove";

export interface SequenceMigrationFlag {
  code: string;
  message: string;
  sequenceType?: BotSequenceType;
  scopeLabel?: string;
  offsetDays?: number;
}

export interface SequenceMigrationDay {
  offsetDays: number;
  enabled: boolean;
  /** "legacy" = the operator's own wording carried over; "default" = Snowy's default copy. */
  wording: "legacy" | "default";
  text: string | null;
  call: boolean;
  email: boolean;
  fmsNote: boolean;
  sendASAP: boolean;
}

export interface SequenceMigrationScopeRow {
  scope: "org" | "group" | "location";
  scopeId: number;
  scopeLabel: string;
  status: SequenceMigrationRowStatus;
  /** Simulated at the org; Off at a group or location with nothing on in legacy; null elsewhere. */
  mode: BotSequenceMode | null;
  /** Nothing is on in legacy here while the scope above has days: written as mode Off, no days. */
  off: boolean;
  days: SequenceMigrationDay[];
  changes: string[];
}

/** One location's days in Snowy after Apply, how they reach it, and how they differ from the org's. */
export interface SequenceMigrationLocationRow {
  locationId: number;
  locationName: string;
  groupName: string | null;
  days: SequenceMigrationDay[];
  /** org/group = inherits that row; own = its own row; off = its own row with mode Off; kept = Snowy's own setup. */
  writtenAs: "org" | "group" | "own" | "off" | "kept";
  /** Plain-English differences from the org's days ("Day 5: no call"); empty when it matches. */
  differences: string[];
}

export interface SequenceMigrationScheduleRow {
  scope: "org" | "group" | "location";
  scopeId: number;
  scopeLabel: string;
  status: SequenceMigrationRowStatus;
  label: string;
  hours: string;
}

export interface SequenceMigrationPreviewType {
  sequenceType: BotSequenceType;
  label: string;
  /** "ready" = migrates; "live" = On in Snowy somewhere, never touched; "none" = no legacy rows;
   * "off" = legacy rows, but no day is On anywhere, so nothing migrates. */
  status: "ready" | "live" | "none" | "off";
  rows: SequenceMigrationScopeRow[];
  /** Every location (sorted by name), empty unless "ready". */
  locations: SequenceMigrationLocationRow[];
  schedules: SequenceMigrationScheduleRow[];
  flags: SequenceMigrationFlag[];
  writeCount: number;
}

/** The flags the PM v3 Sequences tab needs before anyone can review the migrated setup there. */
export interface SequenceMigrationEngineFlags {
  sequencesPage: boolean;
  engine: boolean;
  reconciler: boolean;
  scheduler: boolean;
  dispatcher: boolean;
}

export interface SequenceMigrationRun {
  at: string;
  written: number;
  removed: number;
  kept: number;
  flagged: number;
  failed: number;
}

export interface SequenceMigrationPreview {
  types: SequenceMigrationPreviewType[];
  flags: SequenceMigrationFlag[];
  orgNameWrites: { scopeLabel: string; value: string }[];
  engine: SequenceMigrationEngineFlags;
  hasMandrillApp: boolean;
  lastRun: SequenceMigrationRun | null;
}

export interface SequenceMigrationApplyResult {
  written: number;
  removed: number;
  kept: number;
  schedulesWritten: number;
  orgNameWritten: number;
  flagged: number;
  failed: number;
  failures: string[];
}

/** One save of the Unmigrated Content step: when, and what it did to the rollup ticket. */
export interface UnmigratedReviewRun {
  at: string;
  ticketNumber: number | null;
  /** True when this save opened the ticket; false when it updated an open one or none was needed. */
  created: boolean;
  tasksAdded: number;
  actionableCount: number;
  totalCount: number;
}

export interface PolicyMigrationCustomizedItem {
  key: string;
  label: string;
  description: string;
  value: string;
  scope: string;
}

/** One (key, scope) row's own classification — a CortexCustomizationGroup's `locations` entries, one
 * per org/group/location that customized this key. */
export interface CortexCustomizationItem {
  key: string;
  label: string;
  description: string;
  value: string;
  scope: string;
  /** Human label for `scope` — "Org", a location name, or a fallback "Location <id>"/"Group <id>". */
  scopeLabel: string;
  /** Human-readable target(s) this scope's value carries into. Null when this scope doesn't carry over. */
  destination: string | null;
  /** Why this scope's value does NOT carry over. Null when it does. */
  reason: string | null;
  /** "toggle" = a structured value (boolean/enum/select); "free-text" = the operator's own written
   * words — the CX-actionable content this report exists for. */
  kind: "toggle" | "free-text";
  /** True when this key matched at least one migration branch (Details, Policy T1, or Policy T2
   * prose) — a real candidate for a Cortex V3 destination, even when this particular attempt failed.
   * False means no branch has ever heard of this key: never a Details or Policy Builder candidate at
   * all (a plain admin/integration flag, most commonly). */
  hasPotentialDestination: boolean;
}

/** Every scope that customized one legacy key, grouped — one row per FIELD instead of one row per
 * (field, scope). The same "org row + per-location breakdown" shape the Policies review uses. */
export interface CortexCustomizationGroup {
  key: string;
  label: string;
  description: string;
  kind: "toggle" | "free-text";
  /** See `CortexCustomizationItem.hasPotentialDestination` — identical across every location in this group. */
  hasPotentialDestination: boolean;
  /** Non-null only when EVERY scope in `locations` carries over. */
  destination: string | null;
  /** Non-null only when NOT every scope carries over — a representative reason. */
  reason: string | null;
  /** Every org/group/location that customized this key, each with its own destination/reason. */
  locations: CortexCustomizationItem[];
}

export interface CortexCustomizationReport {
  carryingOver: CortexCustomizationGroup[];
  notCarryingOver: CortexCustomizationGroup[];
  /** Same meaning as `PolicyMigrationPreview.scanIncomplete`. */
  scanIncomplete: boolean;
}

/** Advisory-only "where might this go" suggestion for one free-text KEY with no Policy Builder home.
 * One per key (not per scope) — never written anywhere automatically. CX decides. */
/** Outcome of "save this run and raise one ticket" on the Unmigrated Content page. */
export interface UnmigratedReviewResult {
  /** false when an open ticket already existed (deduped) or nothing passed the gate. */
  created: boolean;
  ticketNumber: number | null;
  taskCount: number;
  /** Tasks added to an already-open ticket by this run; 0 when a new ticket was created. */
  tasksAdded: number;
  /** How many judgements were actionable enough to earn a task. */
  actionableCount: number;
  /** Every judgement considered, actionable or not — all of them are archived. */
  totalCount: number;
  /** S3 key of the archived run, or null when the archive write failed (the ticket still stands). */
  archiveKey: string | null;
}

/** What happened to one ticked placement, including WHERE it landed. A per-location write is
 * invisible on the org-level Policies page, so the scope has to be reported, not implied. */
export interface AppliedPlacement extends SuggestedPlacement {
  scope: "org" | "locations" | "none";
  /** Empty for an org-scope write. */
  locationIds: number[];
  applied: boolean;
  error?: string;
}

/** Result of applying ticked placements. Not `PolicyMigrationApplyResult`: that type's
 * `appliedFieldCount` deduplicates a field across every scope, and its `locationCount` omits
 * org-level writes, which together made "6 answers across 8 locations" unreadable. */
export interface PlacementApplyResult {
  placements: AppliedPlacement[];
  appliedPlacementCount: number;
  failedPlacementCount: number;
  /** Actual worksheet writes that succeeded, not deduplicated fields. */
  writeCount: number;
}

/** One placement the operator ticked on the Unmigrated page. Mirrors a matched suggestion's fields;
 * `value` is how the server recovers which scope(s) to write at. */
export interface SuggestedPlacement {
  key: string;
  value: string;
  policyKey: string;
  itemKey: string;
  answer: string;
}

/** The model's own rating of a placement. Only "high" is eligible for the review page's pre-ticked
 * auto-apply; anything else is advisory. An unstated rating degrades to "low". */
export type SuggestionConfidence = "high" | "medium" | "low";

export interface CustomizationSuggestion {
  key: string;
  /** The EXACT value this suggestion judged. A key customized differently per location produces one
   * suggestion per distinct value, so the text shown beside an outcome is always the text it came
   * from. Clients join on `key` + this value. */
  value: string;
  /** Null = no policy clearly fits this content — a normal, expected result for most items. */
  suggestedPolicyKey: string | null;
  suggestedPolicyTitle: string | null;
  /** The structured worksheet item the content answers, e.g. "lien-notice-days". Null when none fits. */
  suggestedItemKey: string | null;
  /** That item's operator-facing label, for display. Null when no item fits. */
  suggestedItemLabel: string | null;
  /** The proposed ANSWER for `suggestedItemKey` (an optionKey, "1"/"0", or plain text/number) —
   * advisory only, never written automatically. Null whenever no item fits. */
  suggestedSentence: string | null;
  /** How sure the model is that `suggestedSentence` answers `suggestedItemKey`. Always "low" when
   * nothing matched. Gates the review page's pre-ticked auto-apply. */
  confidence: SuggestionConfidence;
  /** True when this is legacy bot-script text Snowy Voice already handles conversationally (an
   * acknowledgment/empathy/transition line with no independent fact) rather than policy content with
   * a missing home. Independent of suggestedPolicyKey — plenty of genuine policy facts (a notice
   * period, a fee) have no matching policy either, and are NOT bot-handled filler. */
  isBotHandledFiller: boolean;
  /** Only meaningful when no item fits and `catalogGap` is null: true when it's clear no Cortex V3
   * field — Details or a Policy Builder question — could ever capture this content (a true gap);
   * false when genuinely uncertain and worth a closer human look (needs review). */
  noPlausibleDestination: boolean;
  /** The model's own one-line justification: which question it answers and the words that answer it
   * (or, when nothing matched, why it was triaged the way it was). A placement with no stated
   * reasoning is rejected outright rather than shown, so this is never empty on a match. */
  why: string | null;
  /** Content already substantially captured by something this org is already migrating — no action. */
  alreadyCovered: boolean;
  /** Set when no existing item fits AND the content looks like a question the catalog should have:
   * a one-line description of the item to add. Feeds the product backlog rather than this org's
   * migration, and is the structured alternative to appending free text to a policy. */
  catalogGap: string | null;
}

export class CortexAPI {
  static async getCatalog(): Promise<CortexCatalog> {
    return APIService.Server.get<CortexCatalog>(`/cortex/catalog`);
  }

  static async getTopicVars(orgId: number, topicId: string, groupId?: number, locationId?: number): Promise<CortexTopicResponse> {
    const query: Record<string, number> = {};
    if (groupId != null) query.groupId = groupId;
    if (locationId != null) query.locationId = locationId;
    return APIService.Server.get<CortexTopicResponse>(`/cortex/org/${orgId}/topic/${topicId}`, Object.keys(query).length > 0 ? query : undefined);
  }

  // `orgId` rides in the route so member auth can enforce it (ENG-3497); the body carries the rest.
  static async saveTopic(req: CortexSaveRequest): Promise<CortexSaveResponse> {
    const { orgId, ...body } = req;
    return APIService.Server.post<CortexSaveResponse>(`/cortex/org/${orgId}/save`, body);
  }

  static async saveRaw(req: CortexRawSaveRequest): Promise<CortexSaveResponse> {
    const { orgId, ...body } = req;
    return APIService.Server.post<CortexSaveResponse>(`/cortex/org/${orgId}/save-raw`, body);
  }

  static async previewMigration(orgId: number): Promise<MigrationPreview> {
    return APIService.Server.get<MigrationPreview>(`/cortex/migration/preview`, { orgId });
  }

  static async applyMigration(orgId: number, specIds: string[], overrides: MigrationOverride[] = []): Promise<MigrationApplyResult> {
    return APIService.Server.post<MigrationApplyResult>(`/cortex/migration/apply`, { orgId, specIds, overrides });
  }

  static async migrationHoursSuggestions(values: string[]): Promise<HoursSuggestion[]> {
    return APIService.Server.post<HoursSuggestion[]>(`/cortex/migration/hours-suggestions`, { values });
  }

  static async previewPolicyMigration(orgId: number): Promise<PolicyMigrationPreview> {
    return APIService.Server.get<PolicyMigrationPreview>(`/cortex/policy-migration/preview`, { orgId });
  }

  static async applyPolicyMigration(orgId: number, writes: PolicyMigrationWrite[]): Promise<PolicyMigrationApplyResult> {
    return APIService.Server.post<PolicyMigrationApplyResult>(`/cortex/policy-migration/apply`, { orgId, writes });
  }

  /** Persist (or clear) a "Skip this one" decision for one field at one scope (null locationId = org),
   * so it survives a reload/re-run instead of the field reappearing as if never reviewed. */
  static async setPolicyFieldExcluded(orgId: number, locationId: number | null, mapKey: string, excluded: boolean): Promise<void> {
    return APIService.Server.post<void>(`/cortex/policy-migration/exclusion`, { orgId, locationId, mapKey, excluded });
  }

  static async generatePolicyNewFieldsTicket(orgId: number): Promise<PolicyNewFieldsTicketResult> {
    return APIService.Server.post<PolicyNewFieldsTicketResult>(`/cortex/policy-migration/new-fields-ticket`, { orgId });
  }

  static async migrationStatus(orgId: number): Promise<MigrationStatus> {
    return APIService.Server.get<MigrationStatus>(`/cortex/migration/status`, { orgId });
  }

  static async previewSequenceMigration(orgId: number): Promise<SequenceMigrationPreview> {
    return APIService.Server.get<SequenceMigrationPreview>(`/cortex/sequence-migration/preview`, { orgId });
  }

  static async applySequenceMigration(orgId: number, types: BotSequenceType[]): Promise<SequenceMigrationApplyResult> {
    return APIService.Server.post<SequenceMigrationApplyResult>(`/cortex/sequence-migration/apply`, { orgId, types });
  }

  static async policyCustomizedUnmapped(orgId: number): Promise<PolicyMigrationCustomizedItem[]> {
    return APIService.Server.get<PolicyMigrationCustomizedItem[]>(`/cortex/policy-migration/customized-unmapped`, { orgId });
  }

  static async policyCustomizationReport(orgId: number): Promise<CortexCustomizationReport> {
    return APIService.Server.get<CortexCustomizationReport>(`/cortex/policy-migration/customization-report`, { orgId });
  }

  /** Read-only "where might this go" suggestion for the given (already-fetched) free-text
   * not-carrying-over groups — never writes anything; CX decides whether/where to add it.
   * `carryingOverGroups` (the same report's own `carryingOver` list) gives the model visibility into
   * what already migrated for this org, so it can recognize a redundant suggestion instead of
   * drafting one blind. */
  /** Write the operator-confirmed placements from the Unmigrated page into Policy Builder. `groups`
   * comes along because a suggestion is keyed by (legacy key, value) and carries no scope: the server
   * resolves each to org scope when every scope shares the value, and per-location otherwise. */
  static async policyApplySuggestedPlacements(
    orgId: number,
    groups: CortexCustomizationGroup[],
    selections: SuggestedPlacement[]
  ): Promise<PlacementApplyResult> {
    return APIService.Server.post<PlacementApplyResult>(`/cortex/policy-migration/apply-suggested-placements`, { orgId, groups, selections });
  }

  /** Archive this run's unmigrated content to S3 and raise ONE rollup ticket for Swivl CX.
   *
   * `suggestions` is the caller's own merged map of judgements — the server does not recompute them
   * (that would be ~28 LLM calls, well past the request budget, and would yield a second set of
   * judgements differing from the ones on screen). Everything passed is archived; only items that
   * are actionable (a concrete placement, or genuinely unclear) become ticket tasks. */
  static async policyUnmigratedReview(orgId: number, suggestions: CustomizationSuggestion[]): Promise<UnmigratedReviewResult> {
    return APIService.Server.post<UnmigratedReviewResult>(`/cortex/policy-migration/unmigrated-review`, { orgId, suggestions });
  }

  static async policyCustomizationSuggestions(
    orgId: number,
    groups: CortexCustomizationGroup[],
    carryingOverGroups: CortexCustomizationGroup[]
  ): Promise<CustomizationSuggestion[]> {
    return APIService.Server.post<CustomizationSuggestion[]>(`/cortex/policy-migration/customization-suggestions`, {
      orgId,
      groups,
      carryingOverGroups
    });
  }
}
